/** Office index grouping: the flat 36-section list was hard to scan on a phone.
    Pure mapping so a unit test pins exact one-to-one coverage with SECTIONS. */
export interface OfficeSectionLike { id: string; label: string; hint: string }

export const OFFICE_GROUPS: Array<{ label: string; ids: string[] }> = [
  { label: 'Herd and breeding', ids: ['breedlab', 'breeding', 'studbook', 'genetics', 'research', 'dealer', 'contracts', 'buyers', 'meat', 'arrivals'] },
  { label: 'Guests and income', ids: ['guest-map', 'zoo', 'tourism', 'guest-business', 'lodge', 'specials', 'partners', 'standing'] },
  { label: 'Money', ids: ['finance', 'overview', 'forecast', 'price-history', 'insurance', 'taxes', 'credit'] },
  { label: 'Land and environment', ids: ['land', 'vegetation', 'restoration', 'rewilding', 'facilities'] },
  { label: 'People and operations', ids: ['staff', 'careers', 'advisors', 'security', 'identity', 'journal', 'story', 'season', 'reports'] },
];

export interface OfficeSectionGroup<T extends OfficeSectionLike> { label: string; sections: T[] }

/** Group sections for the Office index; an optional query filters by label or hint.
    Sections missing from OFFICE_GROUPS still render under a fallback group - a
    grouping gap must never hide a real section. */
export function groupOfficeSections<T extends OfficeSectionLike>(sections: readonly T[], query = ''): OfficeSectionGroup<T>[] {
  const q = query.trim().toLowerCase();
  const match = (s: T): boolean => q === '' || s.label.toLowerCase().includes(q) || s.hint.toLowerCase().includes(q);
  const out: OfficeSectionGroup<T>[] = [];
  for (const g of OFFICE_GROUPS) {
    const secs = g.ids
      .map((id) => sections.find((s) => s.id === id))
      .filter((s): s is T => s !== undefined && match(s));
    if (secs.length > 0) out.push({ label: g.label, sections: secs });
  }
  const grouped = new Set(OFFICE_GROUPS.flatMap((g) => g.ids));
  const rest = sections.filter((s) => !grouped.has(s.id) && match(s));
  if (rest.length > 0) out.push({ label: 'More', sections: [...rest] });
  return out;
}
